import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  ReactNode,
  MouseEvent,
  Fragment
} from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import { Button, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { XIcon, ChevronDownIcon } from '@csrc/assets/icons'
import { CanCall } from '@modules/user'
import { trans, uuidv4 } from '@utils'

/**
 * Option descriptor for dropdown buttons
 */
export interface ButtonOption {
  title: ReactNode
  icon?: ReactNode
  value: any
  tooltip?: string
  btnTextClassName?: string
}

/**
 * Shape of each action button
 */
export interface ActionBarButton {
  type?: 'button' | 'button-dropdown'
  color?: string
  action?: string
  disabled?: boolean
  onClick?: any
  closeOnClick?: boolean
  hidden?: boolean
  title: ReactNode
  tooltip?: string
  icon?: ReactNode
  value?: any
  options?: ButtonOption[]
  btnTextClassName?: string
}

/**
 * Imperative methods exposed by the ActionBar
 */
export interface ActionBarHandle {
  show(): void
  hide(): void
  getVisibility(): boolean
  setShow(cond: boolean): void
}

/**
 * Props accepted by the ActionBar component
 */
export interface ActionBarProps {
  container?: string
  onClose?: () => void
  numOfSelected?: number
  initShow?: boolean
  showAllSelected?: boolean
  numOfAllSelected?: number
  onClickAllSelected?: (e: MouseEvent) => void
  onClearAllSelected?: (e: MouseEvent) => void
  btnProps?: Record<string, any>
  buttons?: ActionBarButton[]
  btnWrapperClassName?: string
  innerChildren?: ReactNode
  children?: ReactNode
  customText?: string
}

interface RenderedButtonProps {
  button: ActionBarButton
  show?: boolean,
  setShow: (cond: boolean) => void
  className?: string
}

const RenderedButton: React.FC<RenderedButtonProps> = ({ button, show, setShow, className }) => {
  const {
    type = 'button',
    color = 'white',
    action,
    disabled,
    closeOnClick,
    onClick,
    hidden,
    title,
    tooltip,
    value,
    options,
    icon,
    btnTextClassName = ''
  } = button

  const handle = (e: MouseEvent, args?: any) => {
    onClick(e, args)
    if (closeOnClick) setShow(false)
  }

  const uuid = uuidv4().replace(/-/g, '')
  const btnId = `btn${uuid}`

  return (
    <>
      <Button
        id={btnId}
        size="sm"
        color={color}
        disabled={disabled}
        onClick={(e: React.MouseEvent<Element, globalThis.MouseEvent>) => handle(e, { value })}
        className={classnames(`${className} align-items-center`, { hidden, 'd-flex': !hidden })}
      >
        {icon}
        <span className={classnames(btnTextClassName, { 'ml-25': !!icon })}>{title}</span>
      </Button>
      {show && tooltip && (
        <UncontrolledTooltip placement="top" target={btnId}>
          {tooltip}
        </UncontrolledTooltip>
      )}
    </>
  )
}

type RenderButtonProps = RenderedButtonProps

export const RenderButton: React.FC<RenderButtonProps> = (props) => {
  const { button, setShow, className } = props
  const { type = 'button', action, disabled, hidden } = button

  if (type === 'button') {
    const content = <RenderedButton {...props} />
    return action ? <CanCall action={action}>{content}</CanCall> : content
  }

  // button-dropdown
  const dropdown = (
    <UncontrolledButtonDropdown
      className={classnames(className, { hidden })}
      group
      direction="up"
      size="sm"
      disabled={disabled}
    >
      <RenderedButton {...props} />
      <DropdownToggle className="dropdown-toggle-split" color={button.color || 'white'}>
        <FontAwesomeIcon icon={ChevronDownIcon} />
      </DropdownToggle>
      <DropdownMenu right>
        {button.options?.map((opt) => (
          <DropdownItem
            key={String(opt.value)}
            className="d-flex align-items-center w-100"
            title={opt.tooltip}
            onClick={(e: any) => props.button.onClick(e, { value: opt.value })}
          >
            {opt.icon}
            <span className={classnames(opt.btnTextClassName, { 'ml-25': !!opt.icon })}>
              {opt.title}
            </span>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </UncontrolledButtonDropdown>
  )

  return action ? <CanCall action={action}>{dropdown}</CanCall> : dropdown
}

const ActionBar = forwardRef<ActionBarHandle, ActionBarProps>((props, ref) => {
  const [show, setShow] = useState(!!props.initShow)
  const [allSelected, setAllSelected] = useState(false)

  const container = props.container ? document.querySelector(props.container) : null

  useImperativeHandle(ref, () => ({
    show: () => setShow(true),
    hide: () => {
      setShow(false)
      setAllSelected(false)
    },
    getVisibility: () => show,
    setShow
  }))

  const {
    onClose = () => { },
    numOfSelected = 0,
    showAllSelected = false,
    numOfAllSelected = 0,
    onClickAllSelected = () => { },
    onClearAllSelected = () => { },
    btnProps = {},
    buttons = [],
    btnWrapperClassName = '',
    innerChildren = null,
    children,
    customText
  } = props

  const content = (
    <div
      className={classnames('action-bar-wrapper bottom-0 px-2 w-100 bg-primary', {
        'fixed-bottom': show,
        hidden: !show
      })}
      style={{ height: 45, zIndex: 1040 }}
    >
      {innerChildren ?? (
        <div className="position-relative h-100 w-100 d-flex justify-content-between align-items-center">
          <div>
            <span className="text-white font-weight-bolder">
              {
                customText ?? (!allSelected ? trans('gen.actionBar.itemsSelected', { numOfSelected }) : trans('gen.actionBar.allItemsSelected', { numOfAllSelected }))
              }
            </span>
            {showAllSelected && numOfAllSelected > 0 && (
              <Fragment>
                <span className="text-white font-weight-bolder mx-1">|</span>
                {!allSelected ? (
                  <a
                    className="text-white font-weight-bolder"
                    onClick={(e) => {
                      setAllSelected(true)
                      onClickAllSelected(e)
                    }}
                  >
                    <u>{trans('gen.actionBar.selectAllItems', { numOfAllSelected })}</u>
                  </a>
                ) : (
                  <a
                    className="text-white font-weight-bolder"
                    onClick={(e) => {
                      onClearAllSelected(e)
                    }}
                  >
                    <u>{trans('gen.actions.clearAll')}</u>
                  </a>
                )}
              </Fragment>
            )}
          </div>
          <div className={`d-flex justify-content-end align-items-between mr-1 flex-1 ${btnWrapperClassName}`}>
            {buttons.map((btn, i) => (
              <RenderButton key={i} button={btn} show={show} setShow={setShow} className={i > 0 ? 'ml-25' : ''} />
            ))}
          </div>
          <div>
            <Button
              className="btn-icon ml-50 rounded-circle"
              size="sm"
              color="white"
              onClick={onClose}
              {...btnProps}
            >
              <FontAwesomeIcon icon={XIcon} style={{ fontSize: 10 }} className="m-0 aspect-ratio-1" />
            </Button>
          </div>
        </div>
      )}
      <div className="hidden">{children}</div>
    </div>
  )

  return container ? ReactDOM.createPortal(content, container) : content
})

export default ActionBar
